import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { saveMemberDB, getMembersDB, getTransactionsDB, getGelanggangDB } from '../../services/db';
import { PageHeader } from '../../components/common/PageHeader';
import { FinancialTransaction, GelanggangDetail, getGelanggangSessions } from '../../types';
import { isValidEmail, sanitizeIc, sanitizePhone, sanitizeMemberId, isSuperAdmin, findDuplicateIcMember, findDuplicatePhoneMember, toTitleCase, hasRolePermission, getHighestRole, getUserRoles, hasUserRole } from '../../utils/formatters';
import { 
  User, 
  Lock, 
  Save, 
  ShieldCheck, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  Award, 
  Receipt, 
  Clock, 
  CheckCircle2, 
  UserCheck, 
  HelpCircle,
  Building,
  Camera,
  Upload,
  FolderCheck,
  Loader2,
  X,
  Navigation,
  Edit2,
  Plus,
  Eye,
  EyeOff,
  KeyRound
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import { updatePassword } from 'firebase/auth';
import { uploadMemberPhotoToAhliDriveJson, getMemberDriveDirectory } from '../../lib/gdrive';
import { getGoogleMapsUrl, getGelanggangDisplayAddress, formatPublicName } from '../../utils/formatters';

export const AccountPage: React.FC = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const { showToast } = useToast();

  const getInitialSubTab = (): 'personal' | 'bengkung' | 'payments' => {
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname.toLowerCase();
      if (pathname.includes('/account/bengkung')) return 'bengkung';
      if (pathname.includes('/account/payments')) return 'payments';
      if (pathname.includes('/account/personal')) return 'personal';
    }
    return 'personal';
  };

  const [activeSubTab, setActiveSubTabState] = useState<'personal' | 'bengkung' | 'payments'>(getInitialSubTab);

  const handleSubTabChange = (tab: 'personal' | 'bengkung' | 'payments') => {
    setActiveSubTabState(tab);
    const targetPath = tab === 'personal' ? '/account' : `/account/${tab}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };
  const [memberTransactions, setMemberTransactions] = useState<FinancialTransaction[]>([]);
  const [gelanggangDetails, setGelanggangDetails] = useState<GelanggangDetail[]>([]);

  if (!currentUser) {
    return (
      <div className="p-8 text-center text-slate-500 text-xs font-bold">
        Sila log masuk untuk mengakses tetapan akaun anda.
      </div>
    );
  }

  // Check if current user is executive management or superadmin
  const isManagement = isSuperAdmin(currentUser) || hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari']);
  const isPureWaris = currentUser && hasUserRole(currentUser, 'Waris') && !getUserRoles(currentUser).some(r => r !== 'Waris') && !(currentUser as any).isAlsoPesilat;

  // Form State
  const [name, setName] = useState<string>(currentUser.name || '');
  const [icNo, setIcNo] = useState<string>(currentUser.icNo || '');
  const [email, setEmail] = useState<string>(currentUser.email || '');
  const [phone, setPhone] = useState<string>(currentUser.phone || '');
  const [address, setAddress] = useState<string>(currentUser.address || '');
  const [occupation, setOccupation] = useState<string>(currentUser.occupation || '');
  const [photoUrl, setPhotoUrl] = useState<string>(currentUser.photoUrl || '');
  
  // Emergency Contact (Waris)
  const [kinName, setKinName] = useState<string>(currentUser.kinName || '');
  const [kinPhone, setKinPhone] = useState<string>(currentUser.kinPhone || '');
  const [sessionTime, setSessionTime] = useState<string>(currentUser.sessionTime || '');

  // Password State
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState<boolean>(false);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
  const [isProfilePicModalOpen, setIsProfilePicModalOpen] = useState<boolean>(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Scroll lock for Profile Picture Modal
  useEffect(() => {
    if (isProfilePicModalOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'unset';
    };
  }, [isProfilePicModalOpen]);

  const handlePhotoFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila pilih fail imej (PNG, JPG, JPEG, WEBP).');
      return;
    }

    setIsUploadingPhoto(true);

    try {
      // Store in Google Drive Ahli JSON vault (gdrive-ahli.json) under member single directory ASMM/Ahli/<icNo>/
      const uploadedDataUrl = await uploadMemberPhotoToAhliDriveJson(file, currentUser.icNo || currentUser.id, currentUser.name);

      setPhotoUrl(uploadedDataUrl);

      const updatedProfile = {
        ...currentUser,
        photoUrl: uploadedDataUrl
      };

      await saveMemberDB(updatedProfile);
      setCurrentUser(updatedProfile);

      setIsUploadingPhoto(false);
      const targetDir = getMemberDriveDirectory(currentUser.icNo || currentUser.id);
      showToast(

        'success',
        'Gambar Profil Dimuat Naik',
        `Gambar profil berjaya disimpan dalam gdrive-ahli.json di direktori "${targetDir}"!`
      );
    } catch (err) {
      setIsUploadingPhoto(false);
      showToast('error', 'Gagal Muat Naik', 'Ralat ketika menyimpan gambar ke Google Drive Ahli.');
    }
  };

  // Keep form fields synced whenever currentUser updates in AuthContext
  useEffect(() => {
    if (!currentUser) return;
    setName(currentUser.name || '');
    setIcNo(currentUser.icNo || '');
    setEmail(currentUser.email || '');
    setPhone(currentUser.phone || '');
    setAddress(currentUser.address || '');
    setOccupation(currentUser.occupation || '');
    setPhotoUrl(currentUser.photoUrl || '');
    setKinName(currentUser.kinName || '');
    setKinPhone(currentUser.kinPhone || '');
    setSessionTime(currentUser.sessionTime || '');
  }, [
    currentUser?.name,
    currentUser?.icNo,
    currentUser?.email,
    currentUser?.phone,
    currentUser?.address,
    currentUser?.occupation,
    currentUser?.photoUrl,
    currentUser?.kinName,
    currentUser?.kinPhone,
    currentUser?.sessionTime
  ]);

  // Fetch the latest fresh member record directly from DB on mount & on asmm_db_updated
  useEffect(() => {
    let isMounted = true;
    const fetchFreshProfile = async () => {
      if (!currentUser?.id && !currentUser?.icNo) return;
      try {
        const members = await getMembersDB();
        const currentIc = sanitizeIc(currentUser.icNo);
        const fresh = members.find(m => 
          (currentUser.id && m.id && m.id.toUpperCase() === currentUser.id.toUpperCase()) ||
          (currentIc && sanitizeIc(m.icNo) === currentIc)
        );
        if (fresh && isMounted) {
          setName(fresh.name || '');
          setIcNo(fresh.icNo || '');
          setEmail(fresh.email || '');
          setPhone(fresh.phone || '');
          setAddress(fresh.address || '');
          setOccupation(fresh.occupation || '');
          setPhotoUrl(fresh.photoUrl || '');
          setKinName(fresh.kinName || '');
          setKinPhone(fresh.kinPhone || '');
          setSessionTime(fresh.sessionTime || '');

          if (
            fresh.address !== currentUser.address ||
            fresh.phone !== currentUser.phone ||
            fresh.name !== currentUser.name ||
            fresh.email !== currentUser.email ||
            fresh.role !== currentUser.role ||
            fresh.bengkung !== currentUser.bengkung ||
            fresh.gelanggang !== currentUser.gelanggang
          ) {
            setCurrentUser({ ...currentUser, ...fresh });
          }
        }
      } catch (err) {
        console.warn('Failed to fetch fresh profile in AccountPage:', err);
      }
    };

    fetchFreshProfile();
    window.addEventListener('asmm_db_updated', fetchFreshProfile);
    return () => {
      isMounted = false;
      window.removeEventListener('asmm_db_updated', fetchFreshProfile);
    };
  }, [currentUser?.id, currentUser?.icNo, setCurrentUser]);

  useEffect(() => {
    // Fetch transaction history filtered for this user
    getTransactionsDB().then((txs) => {
      const filtered = txs.filter(
        (t) => t.payerOrPayee.toLowerCase().includes(currentUser.name.toLowerCase()) ||
               t.recordedBy === currentUser.name
      );
      setMemberTransactions(filtered);
    }).catch(() => {});

    // Fetch Gelanggang details
    getGelanggangDB().then(setGelanggangDetails).catch(() => {});

    const handleDbUpdate = () => {
      getGelanggangDB().then(setGelanggangDetails).catch(() => {});
    };
    const handlePopState = () => {
      const pathname = window.location.pathname.toLowerCase();
      if (pathname.includes('/account/bengkung')) setActiveSubTabState('bengkung');
      else if (pathname.includes('/account/payments')) setActiveSubTabState('payments');
      else if (pathname.includes('/account')) setActiveSubTabState('personal');
    };
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('asmm_db_updated', handleDbUpdate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentUser.name]);

  const handleSavePasswordOnly = async () => {
    if (!newPassword.trim()) {
      showToast('error', 'Kata Laluan Diperlukan', 'Sila masukkan kata laluan baharu.');
      return;
    }
    if (newPassword.trim().length < 6) {
      showToast('error', 'Kata Laluan Terlalu Pendek', 'Kata laluan mestilah sekurang-kurangnya 6 aksara.');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('error', 'Kata Laluan Tidak Sepadan', 'Kata laluan baharu dan pengesahan tidak sepadan.');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const updatedProfile = {
        ...currentUser,
        password: newPassword.trim(),
        authProvider: (currentUser.firebaseUid ? 'both' : 'password') as 'password' | 'google' | 'both'
      };

      if (auth.currentUser) {
        try {
          await updatePassword(auth.currentUser, newPassword.trim());
        } catch (fbErr) {
          console.warn('Firebase updatePassword warning (non-fatal):', fbErr);
        }
      }

      await saveMemberDB(updatedProfile);
      setCurrentUser(updatedProfile);
      setNewPassword('');
      setConfirmPassword('');
      setIsUpdatingPassword(false);
      showToast('success', 'Kata Laluan Berjaya Ditetapkan', 'Kata laluan akaun anda telah dikemaskini. Anda boleh log masuk menggunakan kata laluan ini.');
    } catch {
      setIsUpdatingPassword(false);
      showToast('error', 'Gagal Menetapkan Kata Laluan', 'Sila cuba lagi sebentar.');
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate new password if user filled it in
    if (newPassword.trim()) {
      if (newPassword.trim().length < 6) {
        showToast('error', 'Kata Laluan Terlalu Pendek', 'Kata laluan mestilah sekurang-kurangnya 6 aksara.');
        return;
      }
      if (newPassword !== confirmPassword) {
        showToast('error', 'Kata Laluan Tidak Sepadan', 'Kata laluan baharu dan pengesahan tidak sepadan.');
        return;
      }
    }

    const cleanIc = sanitizeIc(icNo);
    const cleanPhone = sanitizePhone(phone);
    const cleanKinPhone = sanitizePhone(kinPhone);
    const cleanEmail = email.trim().toLowerCase();
    const allMembers = await getMembersDB();

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      showToast('error', 'Format Emel Tidak Sah', 'Sila masukkan format alamat emel yang sah.');
      return;
    }

    if (cleanEmail) {
      const duplicateEmail = allMembers.find(m => m.id !== currentUser.id && m.email && m.email.toLowerCase().trim() === cleanEmail);
      if (duplicateEmail) {
        showToast('error', 'Emel Telah Digunakan', 'Emel ini sudah digunakan ahli lain.');
        return;
      }
    }

    if (cleanIc) {
      const duplicateIc = findDuplicateIcMember(cleanIc, currentUser.id, allMembers);
      if (duplicateIc) {
        showToast('error', 'No. IC Telah Digunakan', `No. IC ini sudah didaftarkan kepada ahli lain (${duplicateIc.name} - ${duplicateIc.id}).`);
        return;
      }
    }

    if (cleanPhone) {
      const duplicatePhone = findDuplicatePhoneMember(cleanPhone, currentUser.id, allMembers);
      if (duplicatePhone) {
        showToast('error', 'No. Telefon Telah Digunakan', `No. Telefon ${cleanPhone} sudah digunakan oleh ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}).`);
        return;
      }
    }

    setIsSaving(true);

    const updatedProfile = {
      ...currentUser,
      name: toTitleCase(isManagement || !currentUser.name || currentUser.name === 'Pesilat ASMM' ? name.trim() : currentUser.name),
      icNo: isManagement || !currentUser.icNo ? (cleanIc || currentUser.icNo) : currentUser.icNo,
      email: cleanEmail,
      phone: cleanPhone,
      address: address.trim(),
      occupation: occupation.trim(),
      photoUrl: photoUrl.trim() || currentUser.photoUrl,
      kinName: kinName.trim() ? toTitleCase(kinName.trim()) : '',
      kinPhone: cleanKinPhone,
      sessionTime: sessionTime.trim(),
      password: newPassword.trim() ? newPassword.trim() : currentUser.password,
      authProvider: (currentUser.firebaseUid
        ? (newPassword.trim() || currentUser.password ? 'both' : 'google')
        : (newPassword.trim() || currentUser.password ? 'password' : currentUser.authProvider)) as 'password' | 'google' | 'both' | undefined,
    };

    try {
      if (newPassword.trim() && auth.currentUser) {
        try {
          await updatePassword(auth.currentUser, newPassword.trim());
        } catch (fbErr) {
          console.warn('Firebase updatePassword warning (non-fatal):', fbErr);
        }
      }

      // Update local storage DB & active AuthContext
      await saveMemberDB(updatedProfile);
      setCurrentUser(updatedProfile);

      setNewPassword('');
      setConfirmPassword('');
      setIsSaving(false);

      if (newPassword.trim()) {
        showToast('success', 'Profil & Kata Laluan Dikemaskini', 'Maklumat akaun dan kata laluan anda telah berjaya disimpan.');
      } else {
        showToast('success', 'Profil Dikemaskini', 'Maklumat akaun anda telah berjaya disimpan.');
      }
    } catch {
      setIsSaving(false);
      showToast('error', 'Gagal Mengemaskini Profil', 'Sila cuba lagi sebentar.');
    }
  };

  // Find user's assigned gelanggang info
  const myGelanggangInfo = gelanggangDetails.find(
    (g) => g.name === currentUser.gelanggang
  ) || gelanggangDetails[0];

  return (
    <>
      {/* Hidden File Input for Google Drive Ahli Photo Upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoFileUpload}
        accept="image/*"
        className="hidden"
        style={{ display: 'none' }}
      />

      <div className="w-full space-y-4">
        {/* Page Header */}
        <PageHeader
          badgeText="Profil & Rekod Keahlian"
          badgeIcon={User}
          title="Akaun Saya"
        />

      {/* User Summary Badge Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <div
            className="relative shrink-0 group cursor-pointer"
            onClick={() => setIsProfilePicModalOpen(true)}
            title="Klik untuk lihat atau tukar gambar profil"
          >
            <img
              src={photoUrl || '/default-avatar.svg'}
              alt={currentUser.name}
              className="w-16 h-16 sm:w-18 sm:h-18 rounded-full object-cover border-2 border-slate-200 shadow-sm transition-opacity group-hover:opacity-80"
              onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
            />
            <div className="absolute inset-0 bg-slate-900/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-5 h-5 text-white" />
            </div>
            {isUploadingPhoto ? (
              <div className="absolute inset-0 bg-slate-900/70 rounded-full flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              </div>
            ) : (
              <span className="absolute bottom-0 right-0 w-4 h-4 bg-asmm-red text-white border-2 border-white rounded-full flex items-center justify-center shadow-xs" title="Muat naik gambar">
                <Upload className="w-2.5 h-2.5" />
              </span>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
              <h2 className="text-base sm:text-lg font-extrabold text-slate-900 tracking-tight">
                {currentUser.name}
              </h2>
              <span className="text-[10px] font-extrabold bg-asmm-red text-white px-2.5 py-0.5 rounded-full uppercase">
                {getHighestRole(currentUser)}
              </span>
              {getUserRoles(currentUser).filter(r => r !== getHighestRole(currentUser)).map(extraRole => (
                <span key={extraRole} className="text-[9px] font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200">
                  {extraRole}
                </span>
              ))}
            </div>

            <p className="text-xs text-slate-500 font-medium">
              Gelanggang: <strong className="text-slate-800">{currentUser.gelanggang}</strong> • Bengkung: <strong className="text-asmm-red">{currentUser.bengkung}</strong>
            </p>

            <div className="flex flex-wrap items-center gap-2 pt-0.5 text-[11px] text-slate-500 font-medium">
              <span>ID Ahli: <strong className="font-mono text-slate-800 font-bold bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">{sanitizeMemberId(currentUser.id)}</strong></span>
              <span>•</span>
              <span>No. IC: <strong className="font-mono text-slate-700">{currentUser.icNo || 'Belum Dilengkapkan'}</strong></span>
              <span>•</span>
              <span>Ahli Sejak: <strong className="text-slate-700">{currentUser.joinedDate}</strong></span>
              {currentUser.firebaseUid ? (
                <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[10px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Google Dihubungkan
                </span>
              ) : null}
              {currentUser.password ? (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full text-[10px] font-bold">
                  <Lock className="w-2.5 h-2.5" />
                  Kata Laluan Aktif
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-[10px] font-bold">
                  <Lock className="w-2.5 h-2.5" />
                  Kata Laluan Belum Diset
                </span>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Interactive Sub-Tabs Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 space-y-4">
        <div className={`grid ${isPureWaris ? 'grid-cols-1' : 'grid-cols-2'} gap-2 p-1.5 bg-slate-100 rounded-2xl border border-slate-200`}>
          <button
            onClick={() => handleSubTabChange('personal')}
            className={`py-2.5 px-3 rounded-xl text-xs font-extrabold transition-all flex items-center justify-center gap-2 cursor-pointer ${
              activeSubTab === 'personal'
                ? 'bg-white text-asmm-red shadow-sm border border-slate-200/80'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            <User className="w-4 h-4" />
            <span>Maklumat Peribadi</span>
          </button>

          {!isPureWaris && (
            <button
              onClick={() => handleSubTabChange('bengkung')}
              className={`py-2.5 px-3 rounded-xl text-xs font-extrabold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                activeSubTab === 'bengkung'
                  ? 'bg-white text-asmm-red shadow-sm border border-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
              }`}
            >
              <Award className="w-4 h-4" />
              <span>Kemajuan Bengkung</span>
            </button>
          )}

        </div>

        {/* TAB 1: MAKLUMAT PERIBADI & HUBUNGAN */}
        {activeSubTab === 'personal' && (
          <form onSubmit={handleSaveProfile} className="space-y-5 animate-fade-in">
            {/* Locked Identity Header Notice */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
                <Lock className="w-3.5 h-3.5 text-slate-500" />
                <span>Maklumat Pengesahan Identiti (Kekal)</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Nama Penuh
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isManagement && Boolean(currentUser.name && currentUser.name !== 'Pesilat ASMM')}
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-xs font-bold ${
                      isManagement || !currentUser.name || currentUser.name === 'Pesilat ASMM'
                        ? 'bg-white border-slate-300 focus:border-asmm-red focus:outline-none text-slate-900'
                        : 'bg-slate-100 border-slate-200 text-slate-600 cursor-not-allowed'
                    }`}
                  />
                  {!isManagement && Boolean(currentUser.name && currentUser.name !== 'Pesilat ASMM') && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      Nama penuh ditetap mengikut pendaftaran rasmi. Pertukaran hanya melalui Pentadbiran.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    No. Kad Pengenalan
                  </label>
                  <input
                    type="text"
                    value={icNo}
                    onChange={(e) => setIcNo(e.target.value)}
                    disabled={!isManagement && Boolean(currentUser.icNo)}
                    className={`w-full px-3.5 py-2.5 rounded-xl border text-xs font-mono font-bold ${
                      isManagement || !currentUser.icNo
                        ? 'bg-white border-slate-300 focus:border-asmm-red focus:outline-none text-slate-900'
                        : 'bg-slate-100 border-slate-200 text-slate-600 cursor-not-allowed'
                    }`}
                  />
                  {!isManagement && Boolean(currentUser.icNo) && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      No. IC divalidasi mengikut rekod rasmi persatuan.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Editable Contact Fields */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-asmm-red" />
                    <span>Emel Notifikasi</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contoh: ahmed@asmm.org.my"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-asmm-red" />
                    <span>No. Telefon (WhatsApp)</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="contoh: 019-4920194"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-asmm-red" />
                  <span>Alamat Kediaman</span>
                </label>
                <textarea
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Masukkan alamat surat-menyurat kediaman anda..."
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-medium resize-none"
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5 text-asmm-red" />
                    <span>Pekerjaan / Sektor (Pilihan)</span>
                  </label>
                  <input
                    type="text"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    placeholder="contoh: Swasta / Kerajaan / Pelajar"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-medium"
                  />
                </div>
              </div>

              {/* Emergency Contact (Waris) */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                <div className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-asmm-red" />
                  <span>Kontak Kecemasan / Waris</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Nama Waris / Ibu Bapa</label>
                    <input
                      type="text"
                      value={kinName}
                      onChange={(e) => setKinName(e.target.value)}
                      placeholder="Contoh: Halim Bin Saad"
                      className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-medium focus:border-asmm-red focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">No. Tel Waris</label>
                    <input
                      type="text"
                      value={kinPhone}
                      onChange={(e) => setKinPhone(e.target.value)}
                      placeholder="Contoh: 012-5819201"
                      className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-medium focus:border-asmm-red focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Keselamatan & Kata Laluan Akaun */}
              <div className="p-4 sm:p-5 bg-white rounded-2xl border border-slate-200 space-y-4 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-slate-100 gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center text-asmm-red shrink-0">
                      <KeyRound className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-slate-900">Kata Laluan & Keselamatan Akaun</h3>
                      <p className="text-[11px] text-slate-500 font-medium">
                        Tetapkan kata laluan untuk log masuk portal secara terus menggunakan No. Kad Pengenalan anda
                      </p>
                    </div>
                  </div>
                  {currentUser.password ? (
                    <span className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Kata Laluan Aktif
                    </span>
                  ) : (
                    <span className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      <Lock className="w-3.5 h-3.5 text-amber-600" />
                      Belum Ditetapkan
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Lock className="w-3.5 h-3.5 text-asmm-red" />
                        <span>{currentUser.password ? 'Kata Laluan Baharu' : 'Tetapkan Kata Laluan'}</span>
                      </span>
                      {currentUser.password && (
                        <span className="text-[10px] text-slate-400 font-normal">Kosongkan jika tidak ubah</span>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={currentUser.password ? 'Masukkan kata laluan baharu' : 'Cipta kata laluan (min. 6 aksara)'}
                        className="w-full pl-3.5 pr-10 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-medium transition-colors"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5 text-asmm-red" />
                      <span>Sahkan Kata Laluan {currentUser.password ? 'Baharu' : ''}</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Ulang kata laluan"
                        disabled={!newPassword}
                        className={`w-full pl-3.5 pr-10 py-2.5 rounded-xl border text-xs font-medium transition-colors ${
                          !newPassword
                            ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                            : newPassword === confirmPassword && confirmPassword.length > 0
                            ? 'bg-white border-emerald-300 focus:border-emerald-500 text-slate-900'
                            : 'bg-white border-slate-200 focus:border-asmm-red text-slate-900'
                        }`}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={!newPassword}
                        className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 focus:outline-none cursor-pointer disabled:opacity-40"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {newPassword && confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-[10px] text-red-500 font-semibold mt-1">Kata laluan tidak sepadan.</p>
                    )}
                    {newPassword && confirmPassword && newPassword === confirmPassword && (
                      <p className="text-[10px] text-emerald-600 font-semibold mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Kata laluan sepadan.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 text-[11px] text-slate-500 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span>
                      {currentUser.password 
                        ? 'Kata laluan ini boleh digunakan bersama No. Kad Pengenalan untuk log masuk terus.' 
                        : 'Tetapkan kata laluan supaya anda boleh log masuk portal terus menggunakan No. Kad Pengenalan.'}
                    </span>
                  </div>
                  {newPassword && (
                    <button
                      type="button"
                      onClick={handleSavePasswordOnly}
                      disabled={isUpdatingPassword || !newPassword || newPassword !== confirmPassword}
                      className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-lg transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shrink-0 self-end sm:self-auto"
                    >
                      <Lock className="w-3 h-3 text-amber-400" />
                      <span>{isUpdatingPassword ? 'Menyimpan...' : 'Simpan Kata Laluan'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Form Action Submit Button */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-3 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Menyimpan...' : 'Simpan Perubahan Profil'}</span>
              </button>
            </div>
          </form>
        )}

        {/* TAB 2: KEMAJUAN BENGKUNG & SILIBUS */}
        {activeSubTab === 'bengkung' && !isPureWaris && (
          <div className="space-y-3 animate-fade-in">
            <div className="p-4 bg-white text-slate-900 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-center gap-4 relative overflow-hidden shadow-xs">
              <img
                src={currentUser.bengkungBadge || '/badges/putih1-pesilat-muda.png'}
                alt={currentUser.bengkung}
                className="w-full sm:w-auto h-11 object-contain shrink-0"
              />
              <div className="space-y-1 text-center sm:text-left flex-1">
                <span className="text-[10px] font-extrabold uppercase bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-0.5 rounded-full">
                  Pentauliahan Bengkung Semasa
                </span>
                <h3 className="text-lg font-extrabold text-slate-900">{currentUser.bengkung}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Pencapaian tahap persilatan diiktiraf di bawah Lembaga Jurulatih & Majlis Perguruan PSSASMM.
                </p>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between text-xs font-bold text-slate-800">
                <span>Kemajuan Penguasaan Silibus Bengkung</span>
                <span className="text-asmm-red font-mono">75% Selesai</span>
              </div>
              <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-asmm-red rounded-full w-3/4 transition-all duration-500" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-600 pt-2">
                <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Ujian Bengkung Terakhir</div>
                  <div className="font-extrabold text-slate-900">Lulus (2024)</div>
                </div>
                <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Syarat Bengkung Seterusnya</div>
                  <div className="font-extrabold text-slate-900">Kehadiran 80% + Jurus 7</div>
                </div>
                <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-1">
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Fail Pengiktirafan</div>
                  <div className="font-extrabold text-emerald-700">Disahkan</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* MODAL: VIEW & CHANGE PROFILE PICTURE */}
      {isProfilePicModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
          <div className="bg-white border border-slate-200 w-full max-w-sm rounded-3xl shadow-2xl p-6 space-y-5 relative overflow-hidden text-center text-slate-900">
            <button
              onClick={() => setIsProfilePicModalOpen(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-asmm-red bg-red-50 px-3 py-1 rounded-full border border-red-200">
                Gambar Profil Ahli
              </span>
              <h3 className="text-base font-extrabold text-slate-900">{currentUser.name}</h3>
              <p className="text-xs text-slate-500 font-medium">{getHighestRole(currentUser)} • {currentUser.gelanggang}</p>
            </div>

            {/* High-res Image Preview */}
            <div className="relative mx-auto w-44 h-44 rounded-3xl overflow-hidden border-4 border-slate-100 shadow-md bg-slate-100 flex items-center justify-center">
              <img
                src={photoUrl || '/default-avatar.svg'}
                alt={currentUser.name}
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = '/default-avatar.svg'; }}
              />
              {isUploadingPhoto && (
                <div className="absolute inset-0 bg-slate-900/70 flex flex-col items-center justify-center text-white space-y-2">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                  <span className="text-xs font-bold">Memuat naik...</span>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                disabled={isUploadingPhoto}
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Upload className="w-4 h-4 text-amber-400" />
                <span>Tukar Gambar Baru</span>
              </button>

              <button
                type="button"
                onClick={() => setIsProfilePicModalOpen(false)}
                className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};
